import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The bursar's dashboard: their work queue, their own documents in the
 * approval workflow, the day's collections to cash up, and where the money
 * is. Mirrors apps/dashboard/bursar.py.
 */

export interface Receipt {
  id: number; receipt_number: string; time: string; student: string; admission_number: string; class_name: string
  amount: string; refunded: string; net: string; method: string; method_label: string; reference: string; account: string; invoice: string
}
export interface MethodTotal { method: string; label: string; count: number; amount: string }
export interface MyDocument { document_type: string; id: number; title: string; amount: string | null; workflow_status: "returned" | "submitted" | "verified"; tab: string; updated_at: string | null }
export interface MoneyAccount { id: number; code: string; name: string; balance: string }
export interface BursarDesk {
  generated_at: string; today: string; viewer_role: string
  work: Decision[]
  documents: MyDocument[]
  collections: { day: string; receipts: Receipt[]; by_method: MethodTotal[]; total: string }
  collected_this_month: string
  money_accounts: MoneyAccount[]
}

export const num = (value?: string | number | null) => Number(value || 0);
export const ugx = (value?: string | number | null) => `UGX ${Math.round(num(value)).toLocaleString("en-GB")}`;

/** Where each approval tab lives among the finance pages. */
const TAB_PATHS: Record<string, string> = { credits: "/financial/credit-notes", salary: "/staff" };
export const documentLink = (tab: string) => TAB_PATHS[tab] ?? `/financial/${tab}`;

type Tone = { label: string; dot: string };
export function documentStatus(doc: Pick<MyDocument, "workflow_status">): Tone {
  if (doc.workflow_status === "returned") return { label: "Sent back to you", dot: "bg-red-500" };
  if (doc.workflow_status === "verified") return { label: "With the approver", dot: "bg-indigo-500" };
  return { label: "With the reviewer", dot: "bg-sky-500" };
}
/** Sent back first (they are the bursar's to fix), then the rest as they came. */
export const orderDocuments = (rows: MyDocument[]) => [...rows.filter((row) => row.workflow_status === "returned"), ...rows.filter((row) => row.workflow_status !== "returned")];

/** How a receipt came in, as the till is counted: cash, mobile money, bank, or online. */
export function methodGroup(method: string) {
  if (method === "cash") return "cash";
  if (["mobile_money", "mtn_mobile_money", "airtel_money", "africell_money"].includes(method)) return "mobile";
  if (["bank_transfer", "cheque"].includes(method)) return "bank";
  return "online";
}

export const receiptConfig: RegisterConfig<Receipt> = {
  search: (row) => `${row.receipt_number} ${row.student} ${row.admission_number} ${row.reference} ${row.invoice}`,
  views: [
    { key: "all", label: "All" },
    { key: "cash", label: "Cash", match: (row) => methodGroup(row.method) === "cash" },
    { key: "mobile", label: "Mobile money", match: (row) => methodGroup(row.method) === "mobile" },
    { key: "bank", label: "Bank and cheque", match: (row) => methodGroup(row.method) === "bank" },
    { key: "online", label: "Online", match: (row) => methodGroup(row.method) === "online" },
    { key: "refunded", label: "Refunded", match: (row) => num(row.refunded) > 0 },
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "time", label: "Time received", compare: byText((row) => row.time) },
    { key: "amount", label: "Largest first", compare: reversed(byNumber((row) => row.net)) },
    { key: "learner", label: "Learner A–Z", compare: byText((row) => row.student) },
  ],
};

export const viewerNote = (role: string) =>
  role === "bursar" ? "" : deskViewerNote(role, "bursar", "the bursar's dashboard").replace(" The decisions listed are the ones your own account may take, and appraisals are those assigned to you.", " Drafts and documents listed are your own; the rest is the whole school's.");
